import React, { useState, useEffect, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useAuth } from "../../contexts/AuthContext";
import TransactionApiClient from "../../services/TransactionApiClient";
import { Transaction } from "../../types";

interface TransactionSummaryProps {
  selectedPeriod: string;
  loading: boolean;
  onRefresh: () => void;
  onRefreshComplete?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  onPeriodChange: (period: string) => void;
}

const TransactionSummary: React.FC<TransactionSummaryProps> = ({
  selectedPeriod,
  loading: externalLoading,
  onRefresh,
  onRefreshComplete,
  onLoadingChange,
  onPeriodChange,
}) => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartMetric, setChartMetric] = useState<"volume" | "count">("volume");
  const [chartGrouping, setChartGrouping] = useState<"daily" | "weekly">("daily");

  const isLoading = externalLoading || internalLoading;

  // Get date range based on selected period (1wk, 1mth, 3mth, 6mth, 1yr)
  const getDateRange = (period: string) => {
    const endDate = new Date();
    const startDate = new Date();
    endDate.setDate(endDate.getDate() + 1);

    const daysAgo: Record<string, number> = {
      "1wk": 7,
      "1mth": 30,
      "3mth": 90,
      "6mth": 180,
      "1yr": 365,
    };
    const days = daysAgo[period] ?? 7;
    startDate.setDate(endDate.getDate() - days);

    return {
      start: startDate.toISOString().split("T")[0],
      end: endDate.toISOString().split("T")[0],
    };
  };

  const fetchTransactions = async (
    businessId: string,
    period: string = selectedPeriod,
  ) => {
    if (!businessId) return;

    setInternalLoading(true);
    onLoadingChange?.(true);
    setError(null);

    try {
      // Convert from YYYY-MM-DD to MM/DD/YYYY format for API
      const formatDateForAPI = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };

      const { start, end } = getDateRange(period);
      const apiStartDate = formatDateForAPI(start);
      const apiEndDate = formatDateForAPI(end);

      const transactionData = await TransactionApiClient.fetchTransactions(
        businessId,
        apiStartDate,
        apiEndDate,
      );

      setTransactions(transactionData);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setError("Failed to fetch transactions");
    } finally {
      setInternalLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    if (user?.SelectedBusiness?.id) {
      fetchTransactions(user.SelectedBusiness.id, selectedPeriod);
    }
  }, [user?.SelectedBusiness?.id, selectedPeriod]);

  // Handle refresh from parent – run fetch; loading state is cleared in fetchTransactions finally via onLoadingChange
  useEffect(() => {
    if (!externalLoading || !user?.SelectedBusiness?.id) return;
    fetchTransactions(user.SelectedBusiness.id, selectedPeriod);
  }, [externalLoading, user?.SelectedBusiness?.id]);

  // Calculate summary metrics - only for successful transactions
  const totalSales = transactions
    .filter(
      (t) => t.transType === "sale" && t.status?.toLowerCase() === "success",
    )
    .reduce((sum, t) => sum + t.amount, 0);

  const totalRefunds = transactions
    .filter(
      (t) => t.transType === "refund" && t.status?.toLowerCase() === "success",
    )
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  const totalTips = transactions
    .filter((t) => t.status?.toLowerCase() === "success")
    .reduce((sum, t) => sum + t.tip, 0);

  const transactionCount = transactions.length;
  const successTransactionCount = transactions.filter(
    (t) => t.status?.toLowerCase() === "success",
  ).length;

  // Volume by payment method (transType): sale vs refund
  const byTransType = transactions.reduce(
    (acc, t) => {
      const type = (t.transType || "other").toLowerCase();
      if (!acc[type]) acc[type] = { amount: 0, count: 0 };
      acc[type].count += 1;
      acc[type].amount += t.transType?.toLowerCase() === "refund" ? Math.abs(t.amount) : t.amount;
      return acc;
    },
    {} as Record<string, { amount: number; count: number }>,
  );
  const transTypeRows = [
    { key: "sale", label: "Sale", ...(byTransType.sale || { amount: 0, count: 0 }) },
    { key: "refund", label: "Refund", ...(byTransType.refund || { amount: 0, count: 0 }) },
  ].filter((r) => r.count > 0);

  // Volume by card type
  const byCard = transactions.reduce(
    (acc, t) => {
      const card = (t.card || "").trim() || "Unknown";
      const key = card;
      if (!acc[key]) acc[key] = { amount: 0, count: 0 };
      acc[key].count += 1;
      acc[key].amount += t.transType?.toLowerCase() === "refund" ? 0 : t.amount;
      return acc;
    },
    {} as Record<string, { amount: number; count: number }>,
  );
  const cardTypeRows = Object.entries(byCard)
    .map(([label, data]) => ({ label, ...data }))
    .sort((a, b) => b.amount - a.amount);

  // Volume by terminal identifier (case-insensitive)
  const byTerminalIdentifier = transactions.reduce(
    (acc, t) => {
      const raw = (t.terminalIdentifier || "").trim() || "unknown";
      const key = raw.toLowerCase();
      if (!acc[key]) acc[key] = { amount: 0, count: 0, displayName: key ? key.charAt(0).toUpperCase() + key.slice(1) : "Unknown" };
      acc[key].count += 1;
      acc[key].amount += t.transType?.toLowerCase() === "refund" ? 0 : t.amount;
      return acc;
    },
    {} as Record<string, { amount: number; count: number; displayName: string }>,
  );
  const terminalIdentifierRows = Object.entries(byTerminalIdentifier)
    .map(([_, data]) => ({ label: data.displayName, amount: data.amount, count: data.count }))
    .sort((a, b) => b.amount - a.amount);

  // Volume by request origin (for pie chart) – group case-insensitively
  const byRequestOrigin = transactions.reduce(
    (acc, t) => {
      const raw = (t.requestOrigin || "").trim() || "unknown";
      const key = raw.toLowerCase();
      if (!acc[key]) acc[key] = { amount: 0, count: 0, displayName: key ? key.charAt(0).toUpperCase() + key.slice(1) : "Unknown" };
      acc[key].count += 1;
      acc[key].amount += t.transType?.toLowerCase() === "refund" ? 0 : t.amount;
      return acc;
    },
    {} as Record<string, { amount: number; count: number; displayName: string }>,
  );
  const requestOriginRows = Object.entries(byRequestOrigin)
    .map(([key, data]) => ({ name: data.displayName, ...data }))
    .sort((a, b) => b.amount - a.amount);
  const totalRequestOriginAmount = requestOriginRows.reduce((sum, r) => sum + r.amount, 0) || 1;

  const PIE_COLORS = ["#14b8a6", "#0ea5e9", "#8b5cf6", "#f59e0b", "#ef4444", "#64748b", "#06b6d4", "#ec4899"];

  const maxTransTypeAmount = Math.max(...transTypeRows.map((r) => r.amount), 1);
  const maxCardAmount = Math.max(...cardTypeRows.map((r) => r.amount), 1);
  const maxTerminalAmount = Math.max(...terminalIdentifierRows.map((r) => r.amount), 1);
  const totalTransTypeAmount = transTypeRows.reduce((sum, r) => sum + r.amount, 0) || 1;
  const totalCardAmount = cardTypeRows.reduce((sum, r) => sum + r.amount, 0) || 1;
  const totalTerminalAmount = terminalIdentifierRows.reduce((sum, r) => sum + r.amount, 0) || 1;

  // Process data for area chart: per-day success/failed volume and count
  const processAreaChartData = () => {
    const dayMap = new Map<
      string,
      { successAmount: number; failedAmount: number; successCount: number; failedCount: number }
    >();

    transactions.forEach((transaction) => {
      const date = new Date(transaction.date || transaction.authDate);
      const dayKey = date.toISOString().split("T")[0];
      const isSuccess = transaction.status?.toLowerCase() === "success";
      const amount = transaction.transType?.toLowerCase() === "refund" ? Math.abs(transaction.amount) : transaction.amount;

      if (!dayMap.has(dayKey)) {
        dayMap.set(dayKey, {
          successAmount: 0,
          failedAmount: 0,
          successCount: 0,
          failedCount: 0,
        });
      }
      const d = dayMap.get(dayKey)!;
      if (isSuccess) {
        d.successAmount += amount;
        d.successCount += 1;
      } else {
        d.failedAmount += amount;
        d.failedCount += 1;
      }
    });

    return Array.from(dayMap.entries())
      .map(([date, d]) => ({ date, ...d }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  const areaChartData = processAreaChartData();

  // Optionally aggregate by week for chart
  const chartSourceData = useMemo(() => {
    if (chartGrouping !== "weekly" || areaChartData.length === 0) return areaChartData;
    const weekMap = new Map<
      string,
      { successAmount: number; failedAmount: number; successCount: number; failedCount: number; weekStart: string }
    >();
    areaChartData.forEach((d) => {
      const date = new Date(d.date);
      const dayOfWeek = date.getDay();
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - dayOfWeek);
      const key = weekStart.toISOString().split("T")[0];
      if (!weekMap.has(key)) {
        weekMap.set(key, {
          successAmount: 0,
          failedAmount: 0,
          successCount: 0,
          failedCount: 0,
          weekStart: key,
        });
      }
      const row = weekMap.get(key)!;
      row.successAmount += d.successAmount;
      row.failedAmount += d.failedAmount;
      row.successCount += d.successCount;
      row.failedCount += d.failedCount;
    });
    return Array.from(weekMap.values())
      .map((r) => ({ date: r.weekStart, ...r }))
      .sort((a, b) => a.weekStart.localeCompare(b.weekStart));
  }, [areaChartData, chartGrouping]);

  // Recharts format: stacked area uses same stackId for both areas
  const rechartsData = useMemo(
    () =>
      chartSourceData.map((d) => {
        const dt = new Date(d.date);
        const label =
          chartGrouping === "weekly"
            ? `Week of ${dt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
            : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return {
          label,
          date: d.date,
          success: chartMetric === "volume" ? d.successAmount : d.successCount,
          failed: chartMetric === "volume" ? d.failedAmount : d.failedCount,
        };
      }),
    [chartSourceData, chartMetric, chartGrouping],
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="transaction-summary">
        <div className="summary-loading">
          <p>Loading transaction summary...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transaction-summary">
        <div className="summary-error">
          <p style={{ color: "red" }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="transaction-summary">
        <div className="summary-grid">
          <div className="summary-card summary-card--volume">
            <p className="summary-card-label">Volume</p>
            <p className="summary-card-value">{formatCurrency(totalSales)}</p>
            <p className="summary-card-meta">Sales (successful)</p>
          </div>
          <div className="summary-card summary-card--transactions">
            <p className="summary-card-label"># of Payments</p>
            <p className="summary-card-value">{transactionCount}</p>
            <p className="summary-card-meta">{successTransactionCount} successful</p>
          </div>
          <div className="summary-card summary-card--tips">
            <p className="summary-card-label">Total Tips</p>
            <p className="summary-card-value">{formatCurrency(totalTips)}</p>
          </div>
          <div className="summary-card summary-card--refunds">
            <p className="summary-card-label">Total Refunds</p>
            <p className="summary-card-value">{formatCurrency(totalRefunds)}</p>
          </div>
        </div>
      </div>

      <div className="transaction-summary">
        {transactionCount > 0 && areaChartData.length > 0 && (
          <div>
            <div className="chart-header chart-header-tabs">
              <div className="chart-toggles period-toggle-group chart-tabs-left">
                <button
                  type="button"
                  className={`period-toggle-btn ${chartMetric === "volume" ? "active" : ""}`}
                  onClick={() => setChartMetric("volume")}
                  title="Dollar Volume"
                >
                  Dollar Volume
                </button>
                <button
                  type="button"
                  className={`period-toggle-btn ${chartMetric === "count" ? "active" : ""}`}
                  onClick={() => setChartMetric("count")}
                  title="# of Payments"
                >
                  # of Payments
                </button>
              </div>
              <div className="chart-toggles period-toggle-group chart-tabs-right">
                <button
                  type="button"
                  className={`period-toggle-btn ${chartGrouping === "daily" ? "active" : ""}`}
                  onClick={() => setChartGrouping("daily")}
                  title="Daily"
                >
                  Daily
                </button>
                <button
                  type="button"
                  className={`period-toggle-btn ${chartGrouping === "weekly" ? "active" : ""}`}
                  onClick={() => setChartGrouping("weekly")}
                  title="Weekly"
                >
                  Weekly
                </button>
              </div>
            </div>
            <div className="chart-section area-chart-wrap">
              <div className="area-chart" style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={rechartsData}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "var(--dash-text-muted, #64748b)" }}
                      axisLine={{ stroke: "#e5e7eb" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "var(--dash-text-muted, #64748b)" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={
                        chartMetric === "volume"
                          ? (v) => (v >= 1000 ? `$${v / 1000}k` : `$${v}`)
                          : (v) => String(v)
                      }
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        chartMetric === "volume" ? formatCurrency(value) : value,
                        name,
                      ]}
                      labelFormatter={(label) => label}
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: "1px solid var(--dash-card-border, #eee)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="success"
                      name="Successful"
                      stackId="1"
                      stroke="#14b8a6"
                      fill="#14b8a6"
                      fillOpacity={0.35}
                      strokeWidth={1.5}
                    />
                    <Area
                      type="monotone"
                      dataKey="failed"
                      name="Failed"
                      stackId="1"
                      stroke="#ef4444"
                      fill="#ef4444"
                      fillOpacity={0.4}
                      strokeWidth={1.5}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-legend-wrap">
                <span className="chart-legend-title">Series</span>
                <div className="chart-legend">
                  <div className="chart-legend-item">
                    <div
                      className="chart-legend-dot"
                      style={{ backgroundColor: "#14b8a6" }}
                    />
                    <span>Successful</span>
                  </div>
                  <div className="chart-legend-item">
                    <div
                      className="chart-legend-dot"
                      style={{ backgroundColor: "#ef4444" }}
                    />
                    <span>Failed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {transactionCount === 0 && (
          <div className="empty-state">
            No transactions found for {selectedPeriod.toLowerCase()}
          </div>
        )}
      </div>

      {transactionCount > 0 && (transTypeRows.length > 0 || cardTypeRows.length > 0 || terminalIdentifierRows.length > 0 || requestOriginRows.length > 0) && (
        <>
          <div className="transaction-summary breakdown-row breakdown-row-two">
            {transTypeRows.length > 0 && (
              <div className="breakdown-section">
                <h3 className="breakdown-title">Volume by payment method</h3>
                <div className="breakdown-list">
                  {transTypeRows.map((row) => {
                    const sharePct = totalTransTypeAmount ? (row.amount / totalTransTypeAmount) * 100 : 0;
                    return (
                      <div key={row.key} className="breakdown-item">
                        <div className="breakdown-item-header">
                          <span className="breakdown-label">{row.label}</span>
                          <span className="breakdown-meta">
                            <span>{formatCurrency(row.amount)}</span>
                            <span>{sharePct.toFixed(1)}%</span>
                            <span>{row.count} payments</span>
                          </span>
                        </div>
                        <div className="breakdown-bar-track">
                          <div
                            className={`breakdown-bar-fill ${row.key === "sale" ? "breakdown-bar--sale" : "breakdown-bar--refund"}`}
                            style={{
                              width: `${(row.amount / maxTransTypeAmount) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {requestOriginRows.length > 0 && (
              <div className="breakdown-section breakdown-section-pie">
                <h3 className="breakdown-title">Volume by request origin</h3>
                <div className="breakdown-pie-wrap">
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={requestOriginRows.map((r) => ({
                          name: r.name,
                          value: r.amount,
                          count: r.count,
                          pct: totalRequestOriginAmount ? ((r.amount / totalRequestOriginAmount) * 100).toFixed(1) : "0",
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, pct }) => `${name} ${pct}%`}
                      >
                        {requestOriginRows.map((_, index) => (
                          <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, _name: string, props: { payload?: { count?: number }[] }) => {
                          const count = props.payload?.[0]?.count ?? 0;
                          return [formatCurrency(value), `${count} payments`];
                        }}
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 8,
                          border: "1px solid var(--dash-card-border, #eee)",
                        }}
                      />
                      <Legend
                        formatter={(value) => {
                          const row = requestOriginRows.find((r) => r.name === value);
                          const pct = row && totalRequestOriginAmount
                            ? ((row.amount / totalRequestOriginAmount) * 100).toFixed(1)
                            : "0";
                          return `${value} (${pct}%)`;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
          {(cardTypeRows.length > 0 || terminalIdentifierRows.length > 0) && (
            <div className="transaction-summary breakdown-row breakdown-row-two">
              {cardTypeRows.length > 0 && (
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Volume by card type</h3>
                  <div className="breakdown-list">
                    {cardTypeRows.map((row) => {
                      const sharePct = totalCardAmount ? (row.amount / totalCardAmount) * 100 : 0;
                      return (
                        <div key={row.label} className="breakdown-item">
                          <div className="breakdown-item-header">
                            <span className="breakdown-label">{row.label}</span>
                            <span className="breakdown-meta">
                              <span>{formatCurrency(row.amount)}</span>
                              <span>{sharePct.toFixed(1)}%</span>
                              <span>{row.count} payments</span>
                            </span>
                          </div>
                          <div className="breakdown-bar-track">
                            <div
                              className="breakdown-bar-fill breakdown-bar--card"
                              style={{
                                width: `${(row.amount / maxCardAmount) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {terminalIdentifierRows.length > 0 && (
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Volume by terminal</h3>
                  <div className="breakdown-list">
                    {terminalIdentifierRows.map((row) => {
                      const sharePct = totalTerminalAmount ? (row.amount / totalTerminalAmount) * 100 : 0;
                      return (
                        <div key={row.label} className="breakdown-item">
                          <div className="breakdown-item-header">
                            <span className="breakdown-label">{row.label}</span>
                            <span className="breakdown-meta">
                              <span>{formatCurrency(row.amount)}</span>
                              <span>{sharePct.toFixed(1)}%</span>
                              <span>{row.count} payments</span>
                            </span>
                          </div>
                          <div className="breakdown-bar-track">
                            <div
                              className="breakdown-bar-fill breakdown-bar--terminal"
                              style={{
                                width: `${(row.amount / maxTerminalAmount) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionSummary;
